/* List names and the field maps between SharePoint columns and the domain types.

   Every list here is created by provisioning/ledger-site-template.md, and the
   columns marked indexed there are the ones the repositories are allowed to
   filter on (§5, §6).

   Referenced by display name — Graph accepts `lists/{name}` as well as an id, and
   a name that matches the provisioning template is easier to keep honest than a
   GUID copied out of a browser URL. */

import type { DepotId } from '../../models';

export const LISTS = {
  people: 'Blueprint_People',
  groups: 'Blueprint_Groups',
  devices: 'Blueprint_Devices',
  licences: 'Blueprint_Licences',
  applications: 'Blueprint_Applications',
  suppliers: 'Blueprint_Suppliers',
  domains: 'Blueprint_Domains',
  networks: 'Blueprint_Networks',
  locations: 'Blueprint_Locations',
  backups: 'Blueprint_Backups',
  recoveryPlans: 'Blueprint_RecoveryPlans',
  restoreTests: 'Blueprint_RestoreTests',
  articles: 'Blueprint_Articles',
  checklistTemplates: 'Blueprint_ChecklistTemplates',
  checklistRuns: 'Blueprint_ChecklistRuns',
  assignments: 'Blueprint_Assignments',
  credentials: 'Blueprint_Credentials',
  recordTypes: 'Blueprint_RecordTypes',
  records: 'Blueprint_Records',
  modules: 'Blueprint_Modules',
  roles: 'Blueprint_Roles',
  apiKeys: 'Blueprint_ApiKeys',
  webhooks: 'Blueprint_Webhooks',
  statusTiles: 'Blueprint_StatusTiles',
  auditLog: 'Blueprint_AuditLog',
  files: 'Blueprint_Files',
  settings: 'Blueprint_Settings',
} as const;

/** Columns carrying an index. Filtering on anything not in this set is a bug —
    see the note on GraphClient.filterItems. */
export const INDEXED: Record<string, readonly string[]> = {
  [LISTS.people]: ['UPN', 'Depot', 'Status'],
  [LISTS.groups]: ['GroupId', 'GroupType'],
  [LISTS.devices]: ['Title', 'Serial', 'WarrantyEnd', 'AssignedTo', 'Depot'],
  [LISTS.records]: ['Title', 'TypeId', 'Depot', 'ExpiryDate', 'Status'],
  [LISTS.articles]: ['ReviewedOn'],
  [LISTS.suppliers]: ['ContractEnd'],
  [LISTS.assignments]: ['FromDate', 'DeviceRef', 'PersonRef'],
};

/* Depot columns store the site name — §6.1 specifies "Hull / Doncaster /
   London", and these lists get browsed in the SharePoint UI where 'don' means
   nothing to anybody.

   The app used to work in short ids and translate here in both directions,
   which was two spellings of one thing and failed silently: an unrecognised
   name fell back to 'hull', so a depot value somebody added in the SharePoint
   UI would have filed every record at head office without erroring once.

   Now the name is the id, so these are identities. They stay as functions
   because the call sites read better for having a name on the boundary, and
   because a value that is not a string still has to become one rather than
   reaching a screen as undefined. */

/** Whatever the Depot column holds, as a site id. Empty means unfiled, and
    unfiled is a real state — an unassigned laptop belongs nowhere. */
export function toDepotId(value: unknown): DepotId {
  return typeof value === 'string' ? value.trim() : '';
}

/** A site id on its way into a Depot column. */
export function fromDepotId(id: DepotId): string {
  return id;
}

/** Multi-line columns holding JSON. Parsed on read, stringified on write. */
export function parseJsonField<T>(value: unknown, fallback: T): T {
  if (typeof value !== 'string' || value.trim() === '') return fallback;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}

/** SharePoint returns dates as full ISO timestamps; the domain wants dates. */
export function toIsoDate(value: unknown): string {
  if (typeof value !== 'string' || !value) return '';
  return value.slice(0, 10);
}

export function str(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

export function num(value: unknown, fallback = 0): number {
  return typeof value === 'number' ? value : fallback;
}

export function bool(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback;
}
